import { useQuery } from '@apollo/client/react'
import { LinkButton } from '@/atoms/Button'
import { SceneIcon } from '@/atoms/Icons/SceneIcon'
import { ParagraphSM, TitleXS } from '@/atoms/Text'
import { paths } from '@/constants/paths'
import { useCollectiblesPlayerEngagement } from '@/organisms/CollectiblesPlayer/CollectiblesPlayerEngagement/CollectiblesPlayerEngagementContext'
import { getWebClient } from '@/services/ApolloClient'
import { GET_BASIC_COLLECTIBLES_OFFERS_BY_PROJECT } from '@/services/ProjectsService/queries'
import { AvailableCollectibleOffersQuery, CollectibleOffer, CollectibleProductPack } from '@/types/codegen-federation'
import { useLocale } from '@/utils/LocaleUtil'
import { useSafeTrack } from '@/utils/analytics'
import { useTranslate } from '@/utils/translate/translate-client'
import { CollectiblesGoalTrackerBreakdown } from '@/views/CollectiblesViews/CollectiblesGoalTrackerBreakdown'
import { FrameDisplay } from '@/views/CollectiblesViews/CollectiblesPacksViews/CollectiblesPacksView'

interface CollectiblePurchasePromoProps {
  slug: string
}

export const CollectiblePurchasePromo: React.FC<CollectiblePurchasePromoProps> = ({ slug }) => {
  const { t } = useTranslate('watch')
  const { locale } = useLocale()
  const client = getWebClient({ locale })
  const track = useSafeTrack()
  const { engagementMoment } = useCollectiblesPlayerEngagement()

  const { data, loading, error } = useQuery<AvailableCollectibleOffersQuery>(GET_BASIC_COLLECTIBLES_OFFERS_BY_PROJECT, {
    variables: { input: { projectId: slug } },
    client,
    errorPolicy: 'all',
  })

  const offers = data?.availableCollectibleOffers

  const hasAvailableOffers = offers?.some((offer) => offer?.products && offer.products.length > 0)

  const packOffers = (offers as CollectibleOffer[])?.filter((offer) =>
    offer?.products?.some((product) => product?.__typename === 'CollectibleProductPack'),
  ) as CollectibleOffer[]

  const defaultProduct = packOffers?.[0]?.products?.[0] as CollectibleProductPack

  const projectName = getProjectName(slug)

  const trackClick = () => {
    track('Clicked Collectible Side Panel Promo', { slug, engagementMoment })
  }

  if (loading || error || !hasAvailableOffers || slug === 'the-chosen') return null

  const showImagesArray = defaultProduct?.images && (defaultProduct?.images?.length ?? 0) > 0

  return (
    <div className="my-5 w-full rounded-xl bg-core-gray-800 p-4">
      <TitleXS weight="bold">
        {t('purchaseCollectibleV2', 'Purchase a Collectible from {{showTitle}}', { showTitle: projectName })}
      </TitleXS>

      <ParagraphSM color="core-gray-500" className="my-1 leading-5">
        {t(
          'buyADigitalCollectibleSubheader',
          'Buy a Digital Collectible or printed frame for yourself or gift to a loved one.',
        )}
      </ParagraphSM>

      {showImagesArray && defaultProduct?.images ? (
        <div className="p-5">
          <FrameDisplay images={defaultProduct.images.filter((image): image is string => image != null)} />
        </div>
      ) : (
        <div className="p-5">
          <FrameDisplay images={[defaultProduct?.image ?? '']} />
        </div>
      )}
      <CollectiblesGoalTrackerBreakdown slug={slug} />

      <LinkButton
        href={paths.collectibles.shop(slug)}
        variant="oxide-core"
        className="mx-auto my-1"
        target="_blank"
        onClick={trackClick}
      >
        <div className="flex items-center gap-2">
          <SceneIcon color1="transparent" color2="core-gray-950" />
          <TitleXS>{t('fundFutureSeasons', 'Fund Future Seasons')}</TitleXS>
        </div>
      </LinkButton>
    </div>
  )
}

const getProjectName = (slug: string) => {
  switch (slug) {
    case 'wingfeather-saga':
      return 'The Wingfeather Saga'
    case 'tuttle-twins':
      return 'The Tuttle Twins'
    default:
      return 'Angel'
  }
}
